import { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Box, Popover, Typography, IconButton, CircularProgress } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import useMediaQueryHook from "@hooks/useMediaQueryHook";
import stepBack from "@assets/images/stepsBack.jpg";
import { useTranslation } from "react-i18next";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import nlLocale from "date-fns/locale/nl";
import dayjs from "dayjs";
import { useUserData } from "@context/OAuth/AuthContext";
import "dayjs/locale/nl"; // 1. MUST IMPORT THE LOCALE DATA
import { nlNL } from "@mui/x-date-pickers/locales";

const SleepCoachCharts = ({ 
  graphData = {}, 
  shouldDisableDate = null,
  onDateSelect,
  setGraphData,
  loading = false 
}) => {
  const { isMobile } = useMediaQueryHook();
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { t } = useTranslation();
    const { language } = useUserData();
  
  const [activeTab, setActiveTab] = useState("duration");

  const handleOpenCalendar = (event) => {
    setCalendarAnchor(event.currentTarget);
  };

  const handleCloseCalendar = () => {
    setCalendarAnchor(null);
  };

  const handleDateSelect = async (date) => {
    try {
      handleCloseCalendar();
      const formattedDate = dayjs(date).format("YYYY-MM-DD");
      setSelectedDate(date);

      if (onDateSelect && typeof onDateSelect === "function") {
        await onDateSelect(formattedDate);
      }
    } catch (error) {
      console.error("Error selecting date:", error);
    }
  };

  // Function to generate placeholder week data
  const generatePlaceholderWeek = (startDate) => {
    const date = dayjs(startDate || new Date());
    const placeholderData = [];
    
    // Generate 7 days of the week (Sunday to Saturday)
    for (let i = 0; i < 7; i++) {
      const currentDate = date.startOf('week').add(i, 'day');
      const dateString = currentDate.format('YYYY-MM-DD');
      const displayDate = currentDate.format('DD-MM-YYYY');
      
      placeholderData.push({
        name: displayDate,
        originalName: dateString,
        hours: 0,
        awakenings: 0,
        consistency: 0
      });
    }
    
    return placeholderData;
  };

  // Check if arrays are empty
  const hasData = useMemo(() => {
    return (
      graphData?.sleepDuration?.length > 0 ||
      graphData?.sleepAwakening?.length > 0 ||
      graphData?.sleepConsistency?.length > 0
    );
  }, [graphData]);

  const chartData = useMemo(() => {
    // If no data, generate placeholder week
    if (!hasData) {
      return generatePlaceholderWeek(selectedDate);
    }

    const formatDate = (dateString) => {
      if (!dateString) return "";
      try {
        const [year, month, day] = dateString.split("-");
        return `${day}-${month}-${year}`;
      } catch (error) {
        console.warn("Invalid date format:", dateString);
        return dateString;
      }
    };

    let data = [];
    switch (activeTab) {
      case "duration":
        data = graphData?.sleepDuration?.map((item) => ({
          name: formatDate(item?.name),
          originalName: item?.name,
          hours: item?.hours ?? 0,
          awakenings: 0,
          consistency: 0
        })) || generatePlaceholderWeek(selectedDate);
        return data;

      case "awakening":
        data = graphData?.sleepAwakening?.map((item) => {
          let awakenings = 0;
          switch (item.times) {
            case "no_wakeup":
              awakenings = 0;
              break;
            case "1-2_times":
              awakenings = 1;
              break;
            case "2-3_times":
              awakenings = 2;
              break;
            case "3-4_times":
              awakenings = 3;
              break;
            case "more_than_4_times":
              awakenings = 4;
              break;
            default:
              awakenings = 0;
          }
          return {
            name: formatDate(item?.name),
            originalName: item?.name,
            hours: 0,
            awakenings,
            consistency: 0
          };
        }) || generatePlaceholderWeek(selectedDate);
        return data;

      case "consistency":
        data = graphData?.sleepConsistency?.map((item) => ({
          name: formatDate(item?.name),
          originalName: item?.name,
          hours: 0,
          awakenings: 0,
          consistency: item?.consistent === true ? 1 : 0
        })) || generatePlaceholderWeek(selectedDate);
        return data;

      default:
        return generatePlaceholderWeek(selectedDate);
    }
  }, [graphData, activeTab, isMobile, hasData, selectedDate]);

  const getDataKey = () => {
    switch (activeTab) {
      case "duration":
        return "hours";
      case "awakening":
        return "awakenings";
      case "consistency":
        return "consistency";
      default:
        return "hours";
    }
  };

  const calculateChartWidth = () => {
    const dataPoints = chartData.length;
    const minDataPointsForScroll = 5;
    if (dataPoints <= minDataPointsForScroll) return "100%";
    const pointsWidth = dataPoints * (isMobile ? 80 : 100);
    return `${pointsWidth}px`;
  };

  const needsScroll = chartData.length > (isMobile ? 5 : 7);

  const renderXAxisTick = ({ x, y, payload }) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#6B7280"
          fontSize={isMobile ? 10 : 12}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  const getDisableDates = (date) => {
    if (!shouldDisableDate?.length) return false;
    const dateString = dayjs(date).format("YYYY-MM-DD");
    return !shouldDisableDate.includes(dateString);
  };
  

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={language === "nl" ? nlLocale : undefined}
      localeText={
        language === "nl"
          ? nlNL.components.MuiLocalizationProvider.defaultProps.localeText
          : undefined
      }
    >
      <div
        className="p-6 rounded-xl text-white bg-cover h-[100%]"
        style={{ backgroundImage: `url(${stepBack})` }}
      >
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-[16px] font-[Lato] text-white">
            {t("sleepCoachPreferences.Your Sleep Report")}
          </h4>
          
          <IconButton
            onClick={handleOpenCalendar}
            sx={{
              width:"32px",
              height:"32px",
              color: "white",
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "#dbd7d7ff",
              },
              borderRadius:"12px"
            }}
            // disabled={loading}
          >
            {
              loading ? <CircularProgress className="!w-4 !h-4"/> :<CalendarMonthIcon style={{ fontSize: 18, color: "#0575E6" }} />
            }
          </IconButton>
        </div>

        {/* Calendar Popover */}
        <Popover
          open={Boolean(calendarAnchor)}
          anchorEl={calendarAnchor}
          onClose={handleCloseCalendar}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            "& .MuiPopover-paper": {
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
            },
          }}
        >
          <Box sx={{ p: 2, minWidth: isMobile ? 280 : 320 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#0575E6",
                mb: 2,
                fontSize: isMobile ? "1rem" : "1.125rem",
              }}
            >
              {t("Select Date")}
            </Typography>
            
            <DateCalendar
              value={selectedDate}
              onChange={handleDateSelect}
              // shouldDisableDate={getDisableDates}
              disabled={loading}
              sx={{
                "& .MuiPickersDay-root": {
                  fontSize: isMobile ? "0.75rem" : "0.875rem",
                  width: isMobile ? 32 : 36,
                  height: isMobile ? 32 : 36,
                },
                "& .MuiPickersDay-root.Mui-disabled": {
                  color: "text.disabled",
                },
              }}
            />
          </Box>
        </Popover>

        <div className="bg-white rounded-2xl p-6">
          {/* Tabs */}
          <div className="flex flex-col md:flex-row bg-white rounded-full p-1 md:border md:border-[#E9E9E9] w-full gap-1 my-3">
            <button
              onClick={() => setActiveTab("duration")}
              className={`${
                activeTab === "duration"
                  ? "bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white"
                  : "text-gray-600 hover:bg-gray-100"
              } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium flex-1 border border-[#E9E9E9] md:border-none`}
              disabled={loading}
            >
              {t("sleepCoachPreferences.Sleep Duration")}
            </button>

            <button
              onClick={() => setActiveTab("awakening")}
              className={`${
                activeTab === "awakening"
                  ? "bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white"
                  : "text-gray-600 hover:bg-gray-100"
              } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium flex-1 border border-[#E9E9E9] md:border-none`}
              disabled={loading}
            >
              {t("sleepCoachPreferences.Night Awakenings")}
            </button>

            <button
              onClick={() => setActiveTab("consistency")}
              className={`${
                activeTab === "consistency"
                  ? "bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white"
                  : "text-gray-600 hover:bg-gray-100"
              } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium flex-1 border border-[#E9E9E9] md:border-none`}
              disabled={loading}
            >
              {t("sleepCoachPreferences.Bedtime Consistency")}
            </button>
          </div>

            <div className="relative">
              {/* Scrollable container for all screens when data is large */}
              <div
                className={`overflow-x-auto overflow-y-visible ${
                  needsScroll
                    ? "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pb-2"
                    : ""
                }`}
                style={{
                  width: "100%",
                }}
              >
                <div
                  style={{
                    width: calculateChartWidth(),
                    minWidth: "100%",
                  }}
                >
                  <ResponsiveContainer
                    width="100%"
                    height={activeTab === "consistency" ? 100 : 300}
                  >
                    <LineChart
                      data={chartData}
                      margin={
                        isMobile
                          ? { top: 5, right: 10, left: 0, bottom: 25 }
                          : { top: 5, right: 20, left: 0, bottom: 5 }
                      }
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#D6D6D6"
                      />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={renderXAxisTick}
                        interval={
                          needsScroll || isMobile ? "preserveStartEnd" : 0
                        }
                        padding={{ left: 15, right: 15 }}
                        height={isMobile ? 40 : 30}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                        tick={{
                          fill: "#6B7280",
                          fontSize: isMobile ? 10 : 12,
                        }}
                        domain={
                          activeTab === "awakening"
                            ? [0, 4]
                            : activeTab === "consistency"
                            ? [0, 1]
                            : [0, 8]
                        }
                        tickFormatter={(value) => {
                          if (activeTab === "consistency")
                            return value ? "Yes" : "No";
                          return value;
                        }}
                        width={isMobile ? 30 : 40}
                      />

                      <Tooltip
                        formatter={(value) => {
                          if (activeTab === "duration") {
                            return [`${value}h`, t("sleepCoachPreferences.Sleep Duration")];
                          }
                          if (activeTab === "awakening") {
                            return [`${value} ${t("times")}`, t("sleepCoachPreferences.Night Awakenings")];
                          }
                          if (activeTab === "consistency") {
                            return [value ? t("consistent") : t("inconsistent"), t("sleepCoachPreferences.Bedtime Consistency")];
                          }
                        }}
                        labelFormatter={(label, payload) => {
                          // Show original full date in tooltip
                          if (
                            payload &&
                            payload[0] &&
                            payload[0].payload.originalName
                          ) {
                            return payload[0].payload.originalName;
                          }
                          return label;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey={getDataKey()}
                        isAnimationActive={false}
                        stroke="#3B82F6"
                        strokeWidth={3}
                        connectNulls={false}
                        dot={(props) => {
                          const { cx, cy, value } = props;
                          if (value === null || value === undefined) return null;
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={isMobile ? 3 : 5}
                              fill="#3B82F6"
                              stroke="white"
                              strokeWidth={1}
                            />
                          );
                        }}
                        activeDot={(props) => {
                          const { cx, cy, value } = props;
                          if (value === null || value === undefined) return null;
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={isMobile ? 5 : 8}
                              fill="#3B82F6"
                              stroke="white"
                              strokeWidth={2}
                            />
                          );
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Scroll hint for users */}
              {needsScroll && (
                <div className="text-center mt-2">
                  <p className="text-xs text-gray-500">
                    ← {t("Scroll horizontally to see more data")} →
                  </p>
                </div>
              )}
            </div>
          {/* )} */}
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default SleepCoachCharts;

